import { Slot } from "@radix-ui/react-slot";
import { forwardRef, type ComponentProps } from "react";
import { cn } from "@/shared/lib/utils";

interface ToolbarButtonProps extends ComponentProps<"button"> {
  asChild?: boolean;
}

/**
 * Circular icon button used by the contacts toolbar (search, filter). Glass-subtle
 * surface, `rounded-full`, fixed `h-10 w-10`. Per design.md primitives still apply;
 * this is a local composition rather than a new primitive — shared by the toolbar's
 * search and filter controls. Forwards its ref so it can serve as a Radix
 * `asChild` trigger (e.g. the filter dropdown).
 */
export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton({ asChild = false, className, type, ...props }, ref) {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type ?? "button"}
        className={cn(
          "inline-flex h-10 w-10 items-center justify-center rounded-full",
          "glass-surface-subtle text-text-muted",
          "transition-colors duration-150 hover:text-text hover:bg-white/60",
          "disabled:opacity-50 disabled:pointer-events-none",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
          className,
        )}
        {...props}
      />
    );
  },
);
